import { CompetitionResult, CompetitionTypeNameEnum } from '~/apis/ssf';
import { formatBodyWeight, formatResultWeight } from '~/utils/weight';
import { isBenchpressCompetition } from '~/utils/competition-type';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { Table } from '~/components/Table/Table';
import { Link } from '~/components/Link/Link';
import { formatPoints } from '~/utils/points';
import { Fragment } from 'react/jsx-runtime';
import { getRank } from '~/utils/rank';

interface Props {
  category: string;
  weight: string;
  results: CompetitionResult[];
  isLoading: boolean;
  competitionTypeLabel: string;
  competitionTypeName: CompetitionTypeNameEnum;
  pointSystem: string;
}

function Row({
  results,
  placement,
  isBenchCompetition,
}: {
  results: CompetitionResult;
  placement: string | number;
  isBenchCompetition: boolean;
}) {
  return (
    <Table.Row>
      <Table.Col>{placement}</Table.Col>
      <Table.Col>
        <Link to={`/lifters/${results.person.id}`}>
          {results.person.first_name} {results.person.last_name}
        </Link>
      </Table.Col>
      <Table.Col>{results.club.name}</Table.Col>
      <Table.Col>{formatBodyWeight(results.body_weight)}</Table.Col>
      {!isBenchCompetition && <Table.Col>{formatResultWeight(results.squat ?? 0)}</Table.Col>}
      <Table.Col>{formatResultWeight(results.benchpress ?? 0)}</Table.Col>
      {!isBenchCompetition && <Table.Col>{formatResultWeight(results.deadlift ?? 0)}</Table.Col>}
      {!isBenchCompetition && <Table.Col>{formatResultWeight(results.total)}</Table.Col>}
      <Table.Col>{formatPoints(results.points)}</Table.Col>
    </Table.Row>
  );
}

export function CompetitionResultTable({
  category,
  weight,
  results,
  isLoading,
  competitionTypeLabel,
  competitionTypeName,
  pointSystem,
}: Props) {
  const isBenchCompetition = isBenchpressCompetition(competitionTypeName);

  return (
    <Fragment>
      <PageLayout.SubHeading>{`${competitionTypeLabel} - ${category} ${weight}`}</PageLayout.SubHeading>

      <Table bg="even">
        <Table.Head>
          <Table.Row>
            <Table.Col as="th">#</Table.Col>
            <Table.Col as="th">Namn</Table.Col>
            <Table.Col as="th">Förening</Table.Col>
            <Table.Col as="th">Kroppsvikt</Table.Col>
            {!isBenchCompetition && <Table.Col as="th">Knäböj</Table.Col>}
            <Table.Col as="th">Bänkpress</Table.Col>
            {!isBenchCompetition && <Table.Col as="th">Marklyft</Table.Col>}
            {!isBenchCompetition && <Table.Col as="th">Total</Table.Col>}
            <Table.Col as="th" tooltip={`Poängsystem: ${pointSystem}`}>
              Poäng
            </Table.Col>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {isLoading ? (
            <Table.Loading cols={isBenchCompetition ? 6 : 9} />
          ) : (
            results.map((results, index) => (
              <Row
                results={results}
                key={results.id}
                placement={getRank(results, index)}
                isBenchCompetition={isBenchCompetition}
              />
            ))
          )}
        </Table.Body>
      </Table>
    </Fragment>
  );
}
